import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { schema } from '@radiology/api-client';

/**
 * Certificate list configuration — CERT-09 and CERT-10.
 *
 * The CR is explicit that this belongs in the existing Admin Lists area rather
 * than a page of its own ("Extend the existing Admin Lists area... Do not create
 * a separate admin page"), so these hooks are consumed by the System settings
 * screen, not by a new route.
 *
 * The shape follows the CR's recommended fields: Certificate List Name,
 * Certificate Type, Mapped Title, Active. Writes are refused by RLS for anyone
 * who is not a certifications administrator — there is no check here, on
 * purpose.
 */

export interface CertificateList {
  name: string;
  /** The CR's "All Titles" list. Applies to every employee. */
  all_titles: boolean;
  active: boolean;
}

export interface CertificateListItem {
  list_name: string;
  certificate_type: string;
  active: boolean;
}

export interface CertificateListTitle {
  list_name: string;
  title: string;
}

const LISTS = ['certifications', 'certificate_lists'] as const;
const ITEMS = ['certifications', 'certificate_list_items'] as const;
const TITLES = ['certifications', 'certificate_list_titles'] as const;

/**
 * Everything an employee's requirements are derived from moves when any of this
 * changes, so a configuration edit has to drop the requirement queries too.
 */
function invalidateConfiguration(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: LISTS });
  void queryClient.invalidateQueries({ queryKey: ITEMS });
  void queryClient.invalidateQueries({ queryKey: TITLES });
  void queryClient.invalidateQueries({ queryKey: ['certifications', 'my_requirements'] });
  void queryClient.invalidateQueries({ queryKey: ['certifications', 'my_certificate_options'] });
  void queryClient.invalidateQueries({ queryKey: ['certifications', 'my_extra_certificates'] });
}

export function useCertificateLists() {
  const results = useQueries({
    queries: [
      {
        queryKey: LISTS,
        queryFn: async (): Promise<CertificateList[]> => {
          const { data, error } = await schema('certifications')
            .from('certificate_lists')
            .select('*')
            .order('all_titles', { ascending: false })
            .order('name')
            .returns<CertificateList[]>();
          if (error) throw new Error(error.message);
          return data ?? [];
        },
      },
      {
        queryKey: ITEMS,
        queryFn: async (): Promise<CertificateListItem[]> => {
          const { data, error } = await schema('certifications')
            .from('certificate_list_items')
            .select('*')
            .order('certificate_type')
            .returns<CertificateListItem[]>();
          if (error) throw new Error(error.message);
          return data ?? [];
        },
      },
      {
        queryKey: TITLES,
        queryFn: async (): Promise<CertificateListTitle[]> => {
          const { data, error } = await schema('certifications')
            .from('certificate_list_titles')
            .select('*')
            .order('title')
            .returns<CertificateListTitle[]>();
          if (error) throw new Error(error.message);
          return data ?? [];
        },
      },
    ],
  });

  const [lists, items, titles] = results;
  return {
    lists: lists.data ?? [],
    items: items.data ?? [],
    titles: titles.data ?? [],
    isLoading: results.some(r => r.isLoading),
    error: results.find(r => r.error)?.error as Error | undefined,
  };
}

export function useSaveList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CertificateList) => {
      const { error } = await schema('certifications')
        .from('certificate_lists')
        .upsert(input, { onConflict: 'name' });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateConfiguration(queryClient),
  });
}

/**
 * Remove a whole list.
 *
 * The list rows cascade to its items and title mappings. Certificates are NOT
 * touched — they live in a different table and the CR forbids deleting history.
 * The practical effect is that its types stop being required, and anything
 * already held moves to "Other certificates on record".
 */
export function useDeleteList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const { error } = await schema('certifications')
        .from('certificate_lists')
        .delete()
        .eq('name', name);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateConfiguration(queryClient),
  });
}

export function useSaveListItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CertificateListItem) => {
      const { error } = await schema('certifications')
        .from('certificate_list_items')
        .upsert(input, { onConflict: 'list_name,certificate_type' });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateConfiguration(queryClient),
  });
}

export function useDeleteListItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { listName: string; certificateType: string }) => {
      const { error } = await schema('certifications')
        .from('certificate_list_items')
        .delete()
        .eq('list_name', input.listName)
        .eq('certificate_type', input.certificateType);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateConfiguration(queryClient),
  });
}

/** CERT-10 — map a Title to a list. */
export function useMapTitle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CertificateListTitle) => {
      const { error } = await schema('certifications')
        .from('certificate_list_titles')
        .upsert(input, { onConflict: 'list_name,title', ignoreDuplicates: true });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateConfiguration(queryClient),
  });
}

export function useUnmapTitle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CertificateListTitle) => {
      const { error } = await schema('certifications')
        .from('certificate_list_titles')
        .delete()
        .eq('list_name', input.list_name)
        .eq('title', input.title);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateConfiguration(queryClient),
  });
}
